// src/pages/Admin.js
//
// Admin-only page that lists users and lets the admin start an
// impersonation session. The real authorization gate is in the
// `adminListUsers` and `impersonateUser` Cloud Functions; the
// `isAdminEmail(...)` check here is purely cosmetic to avoid showing
// a useless UI to non-admins.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Avatar,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { auth, functions } from '../firebase';
import { isAdminEmail } from '../adminConfig';

// Format an ISO date string as e.g. "May 19, 2026". Returns "—" if missing
// or unparseable so the table cell always renders something.
const formatCreatedAt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const Admin = () => {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [impersonatingUid, setImpersonatingUid] = useState(null);

  const currentEmail = auth.currentUser?.email || '';
  const authorized = isAdminEmail(currentEmail);

  const fetchUsers = useCallback(async (term) => {
    setLoading(true);
    setError(null);
    try {
      const call = httpsCallable(functions, 'adminListUsers');
      const res = await call({ search: term || '' });
      setUsers((res.data && res.data.users) || []);
    } catch (e) {
      console.error('adminListUsers failed:', e);
      setError(e.message || 'Failed to load users.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authorized) fetchUsers('');
  }, [authorized, fetchUsers]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchUsers(search);
  };

  const handleImpersonate = async (targetUid) => {
    if (impersonatingUid) return;
    setImpersonatingUid(targetUid);
    setError(null);
    try {
      const call = httpsCallable(functions, 'impersonateUser');
      const res = await call({ targetUid });
      const customToken = res.data && res.data.customToken;
      if (!customToken) throw new Error('No custom token returned.');
      await signOut(auth);
      await signInWithCustomToken(auth, customToken);
      // Hard reload so every cached hook re-initializes against the new
      // auth state. The banner will render at the top because the new
      // ID token carries the impersonatedBy claim.
      window.location.assign('/history');
    } catch (e) {
      console.error('Impersonation failed:', e);
      setError(e.message || 'Impersonation failed.');
      setImpersonatingUid(null);
    }
  };

  if (!authorized) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Not authorized.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Admin · Impersonation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Search by email, name, or UID and click <strong>Impersonate</strong>{' '}
        to start a session that sees and acts exactly as the user. Every
        impersonation is logged. A red banner will appear at the top of
        every page while you are impersonating.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
        >
          <TextField
            fullWidth
            size="small"
            label="Search by email, name, or UID"
            placeholder="user@example.com or a UID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Search'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>UID</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No users found.
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.uid} hover>
                  <TableCell sx={{ width: 48 }}>
                    <Avatar src={u.photoURL || undefined} sx={{ width: 32, height: 32 }}>
                      {(u.displayName || u.email || '?')[0]?.toUpperCase()}
                    </Avatar>
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-all' }}>{u.email || '—'}</TableCell>
                  <TableCell>{u.displayName || '—'}</TableCell>
                  <TableCell>{u.plan || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCreatedAt(u.createdAt)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                    {u.uid}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<VisibilityIcon />}
                      disabled={!!impersonatingUid}
                      onClick={() => handleImpersonate(u.uid)}
                    >
                      {impersonatingUid === u.uid ? 'Starting…' : 'Impersonate'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Admin;
